import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

/**
 * Lists account cards with 0 linked transactions/documents.
 * All rows start selected; user can deselect before confirming bulk delete.
 */
const DeleteZeroTransactionCardsDialog = ({
    open,
    onClose,
    cards = [],
    loading = false,
    isProcessing = false,
    onConfirmDelete,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (!open) return;
        setSearchQuery('');
        setSelectedIds(new Set((cards || []).map((c) => String(c.cardId))));
    }, [open, cards]);

    const filteredCards = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return cards || [];
        return (cards || []).filter((card) => {
            const id = String(card.cardId ?? '').toLowerCase();
            const name = String(card.name ?? '').toLowerCase();
            const pal = String(card.palCode ?? '').toLowerCase();
            return id.includes(query) || name.includes(query) || pal.includes(query);
        });
    }, [cards, searchQuery]);

    const selectedCount = selectedIds.size;
    const allFilteredSelected =
        filteredCards.length > 0 &&
        filteredCards.every((c) => selectedIds.has(String(c.cardId)));
    const someFilteredSelected =
        filteredCards.some((c) => selectedIds.has(String(c.cardId))) && !allFilteredSelected;

    const handleToggle = (cardId) => {
        const id = String(cardId);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAllFiltered = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filteredCards.forEach((c) => next.add(String(c.cardId)));
            return next;
        });
    };

    const handleDeselectAllFiltered = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filteredCards.forEach((c) => next.delete(String(c.cardId)));
            return next;
        });
    };

    const handleConfirm = () => {
        if (selectedCount === 0 || isProcessing) return;
        onConfirmDelete?.(Array.from(selectedIds));
    };

    return (
        <Dialog
            open={open}
            onClose={isProcessing || loading ? undefined : onClose}
            maxWidth="md"
            fullWidth
            dir={direction}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <DeleteIcon color="error" />
                <Box>
                    <Typography variant="h6" component="span" fontWeight={700}>
                        {t('DeleteZeroTransactionCards') || 'Delete cards with no transactions'}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                        {t('DeleteZeroTransactionCardsHint') ||
                            'All cards with no linked documents are selected. Deselect any you want to keep.'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (cards || []).length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        {t('NoZeroTransactionCards') || 'No account cards with zero transactions found.'}
                    </Typography>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1.5,
                            }}
                        >
                            <TextField
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t('searchAccountCards') || 'Search'}
                                sx={{ minWidth: 220, flex: 1, maxWidth: 360 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box>
                                <Button size="small" onClick={handleSelectAllFiltered} sx={{ mr: 0.5 }}>
                                    {t('SelectAll') || 'Select All'}
                                </Button>
                                <Button size="small" onClick={handleDeselectAllFiltered}>
                                    {t('DeselectAll') || 'Deselect All'}
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={allFilteredSelected}
                                                indeterminate={someFilteredSelected}
                                                onChange={() =>
                                                    allFilteredSelected
                                                        ? handleDeselectAllFiltered()
                                                        : handleSelectAllFiltered()
                                                }
                                                disabled={filteredCards.length === 0 || isProcessing}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <strong>{t('CardID') || 'Card ID'}</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>{t('Name') || 'Name'}</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>{t('PalCode') || 'Pal Code'}</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredCards.map((card) => {
                                        const id = String(card.cardId);
                                        const checked = selectedIds.has(id);
                                        return (
                                            <TableRow
                                                key={id}
                                                hover
                                                selected={checked}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => !isProcessing && handleToggle(id)}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={checked}
                                                        disabled={isProcessing}
                                                        onChange={() => handleToggle(id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </TableCell>
                                                <TableCell>{card.cardId}</TableCell>
                                                <TableCell>{card.name}</TableCell>
                                                <TableCell>{card.palCode || '—'}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            {t('SelectedConflicts') || 'Selected'}: {selectedCount} / {(cards || []).length}
                        </Typography>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} disabled={isProcessing || loading}>
                    {t('cancelButton') || 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={loading || isProcessing || selectedCount === 0}
                    startIcon={
                        isProcessing ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />
                    }
                >
                    {isProcessing
                        ? t('Deleting') || 'Deleting...'
                        : t('DeleteSelectedCount', { count: selectedCount }) ||
                          `Delete selected (${selectedCount})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteZeroTransactionCardsDialog;
